import React, { FC, useEffect, useMemo, useRef, useState, useContext } from "react";

import { FullscreenChartDialog } from "./FullscreenChartDialog";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { getCardBorderStyle } from "@/app/application/theme";
import { QuickRangeToggle } from "@/components/date-range/QuickRangeToggle";
import type { CanonicalQuickRangeKey } from "@/components/date-range/quickRange";
import { StorageKeys } from "@/storage/storageKeys";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
	Box,
	Button,
	ButtonGroup,
	Card,
	CardContent,
	FormControl,
	MenuItem,
	Select,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import { LineChart as ELineChart } from "echarts/charts";
import {
	DataZoomComponent,
	GridComponent,
	LegendComponent,
	TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";

import { ReadFromLocalStorage, WriteToLocalStorage } from "@/service/Utilities";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


echarts.use([
	ELineChart,
	GridComponent,
	LegendComponent,
	TooltipComponent,
	DataZoomComponent,
	CanvasRenderer,
]);

type KpiItem = {
	Name: string;
	Date?: string | null;
	Average?: number;
	Count?: number;
	Required?: number;
};

type KpiField = "Average" | "Count";

type Props = {
	titleKey?: string;
	defaultTitle?: string;
	kpiStatistics: KpiItem[];
	field: KpiField;
	height?: number;
	defaultFrameMs?: number; // unused, kept for compatibility
	legendTopN?: number;
	periodLabel?: string;
	startDate?: Date;
	endDate?: Date;
	selectedRange?: CanonicalQuickRangeKey;
	onRangeChange?: (range: CanonicalQuickRangeKey) => void;
};

const KPI_HEADER_MIN_HEIGHT = 92;
const ALL_METRICS_VALUE = "__all_metrics__";
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const QUICK_RANGE_OPTIONS = [
	{ value: "3m", label: "3m" },
	{ value: "6m", label: "6m" },
	{ value: "1y", label: "1y" },
] as const;

const getKpiFieldValue = (row: KpiItem, field: KpiField): number | null => {
	const value = row[field];
	return typeof value === "number" ? value : null;
};

const formatMetricValue = (value: number | null) =>
	typeof value === "number" && Number.isFinite(value)
		? value.toLocaleString(undefined, {
				maximumFractionDigits: value % 1 === 0 ? 0 : 1,
			})
		: "—";

export const KpiLineRaceChartCard: FC<Props> = ({
	titleKey = "Training.Archive.KpiTrends",
	defaultTitle = "KPI Line",
	kpiStatistics,
	field,
	height = 360,
	periodLabel,
	startDate,
	endDate,
	selectedRange,
	onRangeChange,
}) => {
	const translation: Language | null = useContext(TranslationContext);
	const theme = useTheme();
	const chartRef = useRef<ReactECharts>(null);
	const [openFullscreen, setOpenFullscreen] = useState(false);

	// Available KPI names
	const metricNames = useMemo(
		() => [...new Set(kpiStatistics.map((i) => i.Name))],
		[kpiStatistics],
	);

	const storageKey = useMemo(
		() => StorageKeys.kpiSelectedMetric(field),
		[field],
	);

	const [selectedMetricState, setSelectedMetricState] = useState<string>(
		() => {
			if (typeof window === "undefined") return ALL_METRICS_VALUE;
			const stored = ReadFromLocalStorage<string>(storageKey, ALL_METRICS_VALUE);
			if (stored === ALL_METRICS_VALUE || metricNames.includes(stored)) {
				return stored;
			}
			return ALL_METRICS_VALUE;
		},
	);

	const mainColor = theme.palette.primary.main;
	const tooltipMutedTextColor =
		theme.palette.mode === "dark"
			? "rgba(226,232,240,0.72)"
			: "rgba(71,85,105,0.9)";
	const tooltipShellSx = useMemo(
		() => ({
			renderMode: "html" as const,
			appendToBody: true,
			confine: false,
			enterable: true,
			transitionDuration: 0.08,
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(15, 23, 42, 0.96)"
					: "rgba(255, 255, 255, 0.98)",
			borderColor: alpha(
				theme.palette.primary.main,
				theme.palette.mode === "dark" ? 0.45 : 0.22,
			),
			borderWidth: 1,
			borderRadius: 10,
			padding: [10, 12],
			textStyle: {
				color: theme.palette.text.primary,
				fontSize: 12,
			},
			extraCssText:
				"box-shadow: 0 10px 28px rgba(2,6,23,0.26); max-width: 360px; white-space: normal; z-index: 9999; backdrop-filter: blur(2px);",
		}),
		[theme],
	);

	// Palette for multi-series (fullscreen comparison)
	const chartColors = useMemo(
		() => [
			theme.palette.mode === "dark" ? "#7FB0FF" : "#4F8BFF",
			theme.palette.mode === "dark" ? "#7FCF8A" : "#62B45F",
			theme.palette.mode === "dark" ? "#FFD95F" : "#F2C94C",
			theme.palette.mode === "dark" ? "#FF8A72" : "#E0523D",
			theme.palette.mode === "dark" ? "#8EC5FF" : "#5D9CFF",
			theme.palette.mode === "dark" ? "#9CDEA3" : "#73BF69",
			theme.palette.mode === "dark" ? "#FFE27C" : "#F4D35E",
			theme.palette.mode === "dark" ? "#FF9C88" : "#EB6A56",
			theme.palette.mode === "dark" ? "#93B7FF" : "#6B8CFF",
			theme.palette.mode === "dark" ? "#9FD9A7" : "#7BBE73",
			theme.palette.mode === "dark" ? "#FFD783" : "#F0C75E",
		],
		[theme.palette.mode],
	);

	const resetZoom = () => {
		const inst = chartRef.current?.getEchartsInstance();
		if (!inst) return;
		inst.dispatchAction({ type: "dataZoom", start: 0, end: 100 });
	};

	const metricSeries = useMemo(() => {
		const now = dayjs();
		return metricNames.map((name) => {
			const rowsForMetric = kpiStatistics.filter((row) => row.Name === name);
			const validPoints: { t: Date; v: number; required?: number }[] = [];

			for (const row of rowsForMetric) {
				const raw = row.Date;
				const value = getKpiFieldValue(row, field);
				if (raw == null || value == null) continue;
				const d = dayjs(raw);
				if (!d.isValid()) continue;
				const year = d.year();
				if (year < 2000 || year > now.year() + 1) continue;
				validPoints.push({
					t: d.toDate(),
					v: value,
					required: row.Required,
				});
			}

			validPoints.sort((a, b) => a.t.getTime() - b.t.getTime());
			const points = validPoints.map((point) => [point.t.getTime(), point.v] as [number, number]);

			const slaCandidate = validPoints.find(
				(point) => typeof point.required === "number",
			);

			return {
				name,
				points,
				slaValue: slaCandidate?.required,
				hasData: validPoints.length > 0,
			};
		});
	}, [field, kpiStatistics, metricNames]);

	useEffect(() => {
		setSelectedMetricState((prev) => {
			if (prev === ALL_METRICS_VALUE) {
				return ALL_METRICS_VALUE;
			}

			const selectedSeries = metricSeries.find((series) => series.name === prev);
			return selectedSeries?.hasData ? prev : ALL_METRICS_VALUE;
		});
	}, [metricSeries]);

	// Persist selection
	const selectedMetric = selectedMetricState;
	const setSelectedMetric = setSelectedMetricState;
	useEffect(() => {
		WriteToLocalStorage(storageKey, selectedMetric);
	}, [selectedMetric, storageKey]);

	const activeSeries = useMemo(() => {
		if (selectedMetric === ALL_METRICS_VALUE) {
			return metricSeries.filter((series) => series.hasData);
		}
		return metricSeries.filter(
			(series) => series.name === selectedMetric && series.hasData,
		);
	}, [metricSeries, selectedMetric]);

	const primarySeries = activeSeries[0];
	const xAxisExtent = useMemo(() => {
		const explicitStart = startDate ? dayjs(startDate).startOf("day") : null;
		const explicitEnd = endDate ? dayjs(endDate).endOf("day") : null;

		if (
			explicitStart?.isValid() &&
			explicitEnd?.isValid() &&
			!explicitStart.isAfter(explicitEnd)
		) {
			return {
				min: explicitStart.valueOf(),
				max: explicitEnd.valueOf(),
			};
		}

		const pointTimes = activeSeries.flatMap((series) =>
			series.points.map(([time]) => time),
		);
		if (pointTimes.length === 0) {
			return { min: undefined, max: undefined };
		}

		const minPoint = Math.min(...pointTimes);
		const maxPoint = Math.max(...pointTimes);
		if (minPoint === maxPoint) {
			return {
				min: minPoint - ONE_DAY_MS,
				max: maxPoint + ONE_DAY_MS,
			};
		}

		return {
			min: minPoint,
			max: maxPoint,
		};
	}, [activeSeries, endDate, startDate]);

	const xAxis = useMemo(
		() => ({
			type: "time" as const,
			boundaryGap: false,
			min: xAxisExtent.min,
			max: xAxisExtent.max,
			minInterval: ONE_DAY_MS,
			splitNumber: 6,
		}),
		[xAxisExtent.max, xAxisExtent.min],
	);
	const slaValue =
		selectedMetric === ALL_METRICS_VALUE ? undefined : primarySeries?.slaValue;
	const hasChartData = activeSeries.length > 0;
	const hasSelectableMetrics = metricSeries.some((series) => series.hasData);
	const selectedMetricLabel =
		selectedMetric === ALL_METRICS_VALUE
			? "All KPI metrics"
			: selectedMetric || "Select KPI";

	const chartStats = useMemo(
		() => ({
			pointCount: activeSeries.reduce(
				(total, series) => total + series.points.length,
				0,
			),
		}),
		[activeSeries],
	);

	const controlSurfaceSx = {
		borderRadius: 2.5,
		bgcolor:
			theme.palette.mode === "dark"
				? alpha(theme.palette.common.white, 0.035)
				: alpha(theme.palette.primary.main, 0.03),
		border: `1px solid ${alpha(
			theme.palette.divider,
			theme.palette.mode === "dark" ? 0.34 : 0.72,
		)}`,
	};

	// Base option (theme-aware, single metric)
	const baseOption = useMemo(
		() => ({
			backgroundColor: "transparent",
			color: [mainColor],
			grid: { top: 44, right: 16, bottom: 42, left: 52 },
			animation: false,
			tooltip: {
				...tooltipShellSx,
				trigger: "axis",
				axisPointer: { type: "line" as const },
				formatter: (params: unknown) => {
					const points = (Array.isArray(params) ? params : [params]) as Array<{
						axisValue?: string | number;
						data?: [number, number] | number;
						marker?: string;
						seriesName?: string;
					}>;
					const firstPoint = points[0];
					const axisLabel =
						typeof firstPoint?.axisValue === "number"
							? dayjs(firstPoint.axisValue).format("YYYY-MM-DD")
							: String(firstPoint?.axisValue ?? "");
					const rows = points
						.map((point) => {
							const value =
								Array.isArray(point?.data) && point.data.length > 1
									? Number(point.data[1] ?? 0)
									: Number(point?.data ?? 0);

							return `
                <div style="display:flex; justify-content:space-between; gap:12px;">
                  <span>${point?.marker ?? ""}${point?.seriesName ?? selectedMetricLabel}</span>
                  <strong>${Number.isFinite(value) ? value.toLocaleString() : "-"}</strong>
                </div>
              `;
						})
						.join("");

					return `
            <div style="max-width:300px">
              <div style="font-weight:700; margin-bottom:6px">${axisLabel}</div>
              ${rows}
              <div style="margin-top:6px; color:${tooltipMutedTextColor}; font-size:12px;">
                ${selectedMetricLabel}
              </div>
            </div>
          `;
				},
			},
			legend: {
				show: false,
			},
			xAxis: {
				...xAxis,
				axisLabel: {
					formatter: (v: number) => dayjs(v).format("DD MMM"),
					hideOverlap: true,
					showMinLabel: true,
					showMaxLabel: true,
					color: theme.palette.text.secondary,
					fontSize: 11,
					margin: 12,
				},
				axisLine: {
					lineStyle: { color: alpha(theme.palette.text.primary, 0.14) },
				},
				axisTick: { show: false },
				splitLine: {
					show: false,
				},
			},
			yAxis: {
				type: "value" as const,
				axisLabel: {
					color: theme.palette.text.secondary,
					fontSize: 11,
					margin: 12,
				},
				axisLine: { show: false },
				axisTick: { show: false },
				splitLine: {
					lineStyle: {
						color: alpha(
							theme.palette.text.primary,
							theme.palette.mode === "dark" ? 0.12 : 0.08,
						),
					},
				},
			},
			dataZoom: [
				{ type: "inside" as const, throttle: 50 },
				{
					type: "slider" as const,
					bottom: 2,
					height: 18,
					borderColor: "transparent",
					backgroundColor: alpha(
						theme.palette.text.primary,
						theme.palette.mode === "dark" ? 0.12 : 0.06,
					),
					fillerColor: alpha(theme.palette.primary.main, 0.18),
					handleSize: "85%",
					handleStyle: {
						color: theme.palette.background.paper,
						borderColor: alpha(theme.palette.primary.main, 0.4),
					},
					moveHandleStyle: {
						color: alpha(theme.palette.primary.main, 0.45),
					},
				},
			],
		}),
		[field, mainColor, selectedMetricLabel, theme, tooltipMutedTextColor, tooltipShellSx, xAxis],
	);

	return (
		<Card
			sx={{
				borderRadius: { xs: 2, sm: 3 },
				border: (t) => getCardBorderStyle(t),
				position: "relative",
				overflow: "visible",
				bgcolor: "background.paper",
				boxShadow:
					theme.palette.mode === "dark"
						? "0 1px 0 0 rgba(255,255,255,0.04)"
						: "0 2px 8px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)",
			}}
			>
			{/* Top bar */}
			<Stack
				direction={{ xs: "column", sm: "row" }}
				alignItems={{ xs: "flex-start", sm: "center" }}
				justifyContent="space-between"
				spacing={1.25}
				sx={(t) => ({
					px: 2.25,
					pt: 0.85,
					pb: 0.85,
					minHeight: KPI_HEADER_MIN_HEIGHT,
					bgcolor:
						t.palette.mode === "dark"
							? alpha(t.palette.primary.main, 0.12)
							: alpha(t.palette.primary.main, 0.05),
					borderBottom: 1,
					borderColor: alpha(t.palette.divider, 0.85),
				})}
			>
				<Box sx={{ minWidth: 0 }}>
					<Typography
						variant="caption"
						sx={{
							display: "block",
							letterSpacing: "0.08em",
							fontWeight: 700,
							color: "primary.main",
							mb: 0.05,
							textTransform: "uppercase",
						}}
					>
						<Translate String="Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L444C7.KPIChart">KPI chart</Translate>
					</Typography>
					<Typography
						variant="subtitle1"
						sx={{ fontWeight: 700, lineHeight: 1.05, letterSpacing: "-0.01em" }}
					>
						<Translate String={titleKey}>{defaultTitle}</Translate>
					</Typography>
					{periodLabel && (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ display: "block", mt: 0 }}
						>
							{periodLabel}
						</Typography>
					)}
				</Box>

				<ButtonGroup size="small" variant="outlined" aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L464C61.ChartActions", "chart actions")}>
					<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L465C21.ResetZoom", "Reset zoom")}>
						<Button
							onClick={resetZoom}
							aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L468C19.ResetZoom", "Reset zoom")}
							disabled={!hasChartData}
							sx={{ borderRadius: 2, textTransform: "none" }}
						>
							<RestartAltIcon fontSize="small" />
						</Button>
					</Tooltip>
					<Tooltip title={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L475C21.Fullscreen", "Fullscreen")}>
						<Button
							onClick={() => setOpenFullscreen(true)}
							aria-label={GetLanguageValueAsString(translation, "Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L478C19.OpenFullscreen", "Open fullscreen")}
							disabled={!hasSelectableMetrics}
							sx={{ borderRadius: 2, textTransform: "none" }}
						>
							<FullscreenIcon fontSize="small" />
						</Button>
					</Tooltip>
				</ButtonGroup>
			</Stack>

			<CardContent sx={{ pt: 1.5, px: 2.25, pb: 2 }}>
				<Box
					sx={{
						...controlSurfaceSx,
						p: 1.1,
						mb: 1.25,
					}}
				>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: "block", mb: 0.6, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" }}
					>
						<Translate String="Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L500C7.Metric">Metric</Translate>
					</Typography>
					<FormControl fullWidth size="small">
						<Select
							value={selectedMetric}
							displayEmpty
							onChange={(event) =>
								setSelectedMetric(String(event.target.value))
							}
							disabled={!hasSelectableMetrics}
							sx={{
								borderRadius: 2,
								bgcolor:
									theme.palette.mode === "dark"
										? alpha(theme.palette.common.white, 0.03)
										: theme.palette.background.paper,
								fontWeight: 700,
								"& .MuiSelect-select": {
									display: "flex",
									alignItems: "center",
									gap: 1,
									py: 1.1,
								},
							}}
							renderValue={(value) => (
								<Stack direction="row" spacing={1} alignItems="center">
									<Box
										sx={{
											width: 10,
											height: 10,
											borderRadius: "50%",
											bgcolor: mainColor,
											boxShadow: `0 0 0 4px ${alpha(mainColor, 0.12)}`,
										}}
									/>
									<Typography variant="body2" fontWeight={800} noWrap>
										{value === ALL_METRICS_VALUE
											? "All KPI metrics"
											: typeof value === "string" && value
												? value
												: "Select KPI"}
									</Typography>
								</Stack>
							)}
						>
							<MenuItem value={ALL_METRICS_VALUE} disabled={!hasSelectableMetrics}>
								<Translate String="Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L546C77.AllKPIMetrics">All KPI metrics</Translate>
							</MenuItem>
							{metricSeries.map((series) => (
								<MenuItem
									key={series.name}
									value={series.name}
									disabled={!series.hasData}
								>
									<Stack direction="row" spacing={1} alignItems="center">
										<Box
											sx={{
												width: 8,
												height: 8,
												borderRadius: "50%",
												bgcolor: mainColor,
											}}
										/>
										<span>{series.name}</span>
									</Stack>
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>

				<Stack
					direction={{ xs: "column", sm: "row" }}
					spacing={1}
					alignItems={{ xs: "flex-start", sm: "center" }}
					justifyContent="space-between"
					sx={{
						...controlSurfaceSx,
						px: 1.25,
						py: 1,
						mb: 1.25,
					}}
				>
					<Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
						<Stack direction="row" spacing={0.75} alignItems="center">
							<Box
								sx={{
									width: 18,
									height: 3,
									borderRadius: 999,
									bgcolor: mainColor,
								}}
							/>
							<Typography variant="body2" fontWeight={700}>
								{selectedMetricLabel}
							</Typography>
						</Stack>
						{typeof slaValue === "number" && (
							<Stack direction="row" spacing={0.75} alignItems="center">
								<Box
									sx={{
										width: 18,
										height: 0,
										borderTop: `2px dashed ${theme.palette.error.main}`,
									}}
								/>
								<Typography variant="body2" color="text.secondary" fontWeight={700}>
									Target {formatMetricValue(slaValue)}
								</Typography>
							</Stack>
						)}
					</Stack>

					<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
						{chartStats.pointCount > 0
							? `${chartStats.pointCount} data points`
							: "No data points in range"}
					</Typography>
				</Stack>

				{hasChartData ? (
					<Box
						sx={{
							borderRadius: 3,
							px: { xs: 0.25, sm: 0.75 },
							pt: 0.5,
							pb: 0.25,
							bgcolor:
								theme.palette.mode === "dark"
									? alpha(theme.palette.common.white, 0.015)
									: alpha(theme.palette.primary.main, 0.018),
						}}
					>
						<ReactECharts
							ref={chartRef}
							option={{
								...baseOption,
								series: activeSeries.map((series, idx) => {
									const color = chartColors[idx % chartColors.length];
									return {
										name: series.name,
										type: "line" as const,
										smooth: true,
										showSymbol: false,
										symbolSize: 4,
										data: series.points,
										lineStyle: { width: 1.8, color, cap: "round", join: "round" },
										areaStyle: undefined,
										itemStyle: {
											borderWidth: 1.5,
											borderColor: theme.palette.background.paper,
											color: (params: { data?: unknown }) => {
												const value =
													Array.isArray(params.data) &&
													typeof params.data[1] === "number"
														? params.data[1]
														: null;
												if (
													typeof series.slaValue === "number" &&
													typeof value === "number" &&
													selectedMetric !== ALL_METRICS_VALUE
												) {
													return value > series.slaValue
														? theme.palette.error.main
														: color;
												}
												return color;
											},
										},
										emphasis: {
											scale: 1.12,
											lineStyle: { width: 2.1 },
										},
										markLine:
											typeof series.slaValue === "number" &&
											selectedMetric !== ALL_METRICS_VALUE
												? {
														symbol: "none",
														lineStyle: {
															type: "dashed",
															width: 1.5,
															color: alpha(theme.palette.error.main, 0.9),
														},
														label: {
															formatter: "Target",
															color: theme.palette.error.main,
															fontSize: theme.typography.caption.fontSize,
														},
														data: [{ yAxis: series.slaValue }],
													}
												: undefined,
									};
								}),
							}}
							style={{ width: "100%", height }}
							notMerge
							lazyUpdate
						/>
					</Box>
				) : (
					<Box
						sx={{
							height,
							borderRadius: 2.5,
							border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
							bgcolor:
								theme.palette.mode === "dark"
									? alpha(theme.palette.common.white, 0.02)
									: alpha(theme.palette.primary.main, 0.02),
							display: "grid",
							placeItems: "center",
							px: 3,
							textAlign: "center",
						}}
					>
						<Box sx={{ maxWidth: 320 }}>
							<Typography variant="subtitle2" fontWeight={800}>
								No chart data for this period
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
								{selectedMetric === ALL_METRICS_VALUE
									? "We could not find any KPI metrics with dated values in the selected range."
									: `We could not find any dated KPI values for ${selectedMetric} in the selected range yet.`}
							</Typography>
							{selectedRange && onRangeChange ? (
								<Box
									sx={{
										mt: 2,
										display: "flex",
										flexDirection: "column",
										alignItems: "center",
										gap: 0.9,
									}}
								>
									<Typography variant="caption" color="text.secondary">
										Try another range
									</Typography>
									<QuickRangeToggle
										value={selectedRange}
										onChange={onRangeChange}
										options={QUICK_RANGE_OPTIONS}
										ariaLabel="Choose another KPI date range"
										groupProps={{
											sx: {
												justifyContent: "center",
												"& .MuiToggleButton-root": {
													minWidth: 52,
												},
											},
										}}
									/>
								</Box>
							) : null}
						</Box>
					</Box>
				)}
			</CardContent>

			{/* Fullscreen */}
			<FullscreenChartDialog
				open={openFullscreen}
				onClose={() => setOpenFullscreen(false)}
				translationKey={titleKey}
				defaultText={`${defaultTitle} (Fullscreen)`}
			>
				<Box sx={{ height: 640, display: "flex", flexDirection: "column" }}>
					<Box sx={{ px: 1, pb: 1.25 }}>
						<Box
							sx={{
								...controlSurfaceSx,
								p: 1.1,
								minWidth: { xs: "100%", md: 320 },
							}}
						>
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ display: "block", mb: 0.6, fontWeight: 800 }}
							>
								<Translate String="Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L779C9.Metric">Metric</Translate>
							</Typography>
							<FormControl fullWidth size="small">
								<Select
									value={selectedMetric}
									displayEmpty
									onChange={(event) =>
										setSelectedMetric(String(event.target.value))
									}
									disabled={!hasSelectableMetrics}
									sx={{ borderRadius: 2, fontWeight: 700 }}
								>
									<MenuItem value={ALL_METRICS_VALUE} disabled={!hasSelectableMetrics}>
										<Translate String="Auto.Features.Training.Pages.Dashboard.KpiLineRaceChartCard.L792C79.AllKPIMetrics">All KPI metrics</Translate>
									</MenuItem>
									{metricSeries.map((series) => (
										<MenuItem
											key={series.name}
											value={series.name}
											disabled={!series.hasData}
										>
											{series.name}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					</Box>

					<Box sx={{ flex: 1, minHeight: 0 }}>
						<ReactECharts
							option={{
								...baseOption,
								grid: { top: 104, right: 28, bottom: 64, left: 72 },
								legend: {
									show: true,
									type: "scroll",
									orient: "horizontal",
									top: 12,
									left: 16,
									right: 16,
									itemWidth: 14,
									itemHeight: 10,
									textStyle: {
										fontSize: theme.typography.body2.fontSize,
										color: theme.palette.text.secondary,
									},
									pageIconColor: theme.palette.text.secondary,
									pageTextStyle: { color: theme.palette.text.secondary },
								},
								series: activeSeries.map((series, idx) => {
									const color = chartColors[idx % chartColors.length];
									return {
										name: series.name,
										type: "line" as const,
										smooth: true,
										showSymbol: false,
										data: series.points,
										lineStyle: { width: 1.5, color, cap: "round", join: "round" },
										itemStyle: { color },
									};
								}),
							}}
							style={{ width: "100%", height: "100%" }}
							notMerge
							lazyUpdate
						/>
					</Box>
				</Box>
			</FullscreenChartDialog>
		</Card>
	);
};

export default KpiLineRaceChartCard;
